// 🚀 App - Composant principal de l'application
// Gère le routage, le thème sombre/clair et l'état global
// Utilisé dans : src/main.jsx (point d'entrée)

import { useState, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Navigation from '@/components/layout/Navigation'
import Footer from '@/components/layout/Footer'
import { RadioProvider } from '@/components/RadioPlayer'

// Import des pages publiques
import HomePage from './pages/HomePage'
import CastingPage from './pages/CastingPage'
import AboutPage from './pages/AboutPage'
import PartnersPage from './pages/PartnersPage'
import MentionsLegales from './pages/MentionsLegales'
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite'
import NotFound from './pages/NotFound'

function App() {
  // États pour la gestion du thème
  const [darkMode, setDarkMode] = useState(false)  // État du thème sombre
  const [isAutoMode, setIsAutoMode] = useState(true)  // Mode automatique basé sur l'heure
  const location = useLocation()
  
  // Détection des pages légales (sans navigation/footer)
  const isLegalPage = location.pathname === '/legal' || location.pathname === '/privacy-policy'

  /**
   * Détermine si le thème sombre doit être activé selon l'heure
   * @returns {boolean} true si dark mode (19h-7h), false si light mode
   */
  const shouldBeDark = () => {
    const hour = new Date().getHours()
    // Dark mode de 19h à 7h (inclus), Light mode de 7h à 19h
    return hour >= 19 || hour < 7
  }

  useEffect(() => {
    // Initialisation du thème au chargement de l'application
    // Vérifie les préférences sauvegardées ou applique le mode automatique
    const savedMode = localStorage.getItem('darkMode')
    const savedIsAuto = localStorage.getItem('isAutoMode')
    
    if (savedIsAuto === 'false') {
      // Mode manuel : utiliser la préférence sauvegardée
      setIsAutoMode(false)
      const isDark = savedMode === 'true'
      setDarkMode(isDark)
      if (isDark) {
        document.documentElement.classList.add('dark')
      }
    } else {
      // Mode automatique : détecter selon l'heure
      setIsAutoMode(true)
      const isDark = shouldBeDark()
      setDarkMode(isDark)
      if (isDark) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }

    // Vérification périodique pour le mode automatique
    // Met à jour le thème toutes les minutes si en mode auto
    const interval = setInterval(() => {
      const savedIsAutoNow = localStorage.getItem('isAutoMode')
      if (savedIsAutoNow !== 'false') {
        const shouldBeDarkNow = shouldBeDark()
        setDarkMode(shouldBeDarkNow)
        if (shouldBeDarkNow) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      }
    }, 60000) // Vérifier toutes les 60 secondes

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // Scroll automatique en haut de page lors du changement de route
    // Améliore l'UX en évitant de rester en bas lors de la navigation
    window.scrollTo(0, 0)
  }, [location.pathname])

  /**
   * Bascule manuellement le thème sombre/clair
   * Passe en mode manuel et sauvegarde la préférence utilisateur
   */
  const toggleDarkMode = () => {
    // Passer en mode manuel et basculer
    setIsAutoMode(false)
    const newMode = !darkMode
    setDarkMode(newMode)
    
    // Sauvegarder les préférences dans localStorage
    localStorage.setItem('darkMode', newMode.toString())
    localStorage.setItem('isAutoMode', 'false')
    
    // Appliquer la classe CSS au document
    if (newMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Scroll smooth vers une section spécifique
   * @param {string} id - ID de l'élément à atteindre
   */
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    // Provider Radio pour l'ensemble de l'application
    <RadioProvider>
      <div className="min-h-screen dark:bg-neutral-900 transition-colors duration-300">
        {/* Navigation - Masquée sur les pages légales uniquement */}
        
        {/* Définition des routes de l'application */}
        <Routes>
          {/* Page d'accueil avec navigation et footer */}
          <Route 
            path="/" 
            element={
              <div className="pb-16 md:pb-0">
                <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                <HomePage />
                <Footer />
              </div>
            } 
          />
          
          {/* Page casting avec navigation et footer */}
          <Route 
            path="/casting" 
            element={
              <div className="pb-16 md:pb-0">
                <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                <CastingPage />
                <Footer />
              </div>
            } 
          />
          
          {/* Page à propos avec navigation et footer */}
          <Route 
            path="/about" 
            element={
              <div className="pb-16 md:pb-0">
                <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                <AboutPage />
                <Footer />
              </div>
            } 
          />
          
          {/* Page partenaires avec navigation et footer */}
          <Route 
            path="/partners" 
            element={
              <div className="pb-16 md:pb-0">
                <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                <PartnersPage />
                <Footer />
              </div>
            } 
          />
          
          {/* Pages légales sans navigation ni footer */}
          <Route path="/legal" element={<MentionsLegales />} />
          <Route path="/privacy-policy" element={<PolitiqueConfidentialite />} />
          
          {/* Routes 404 pour les pages non trouvées */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </RadioProvider>
  )
}

export default App
